import type { Locale } from './common';

/**
 * Rendering helpers. Formatting is a view concern and lives only here — the API
 * ships raw values and never a formatted string. Words (month and weekday names)
 * follow the UI language; numbers and `zł` are always pl-PL.
 */

type DateLike = string | number | Date | null | undefined;

const INTL_LOCALE: Record<Locale, string> = { pl: 'pl-PL', en: 'en-GB' };

/** One formatter per UI language, built on first use. */
function perLocale(
  options: Intl.DateTimeFormatOptions,
): (locale: Locale) => Intl.DateTimeFormat {
  const cache = new Map<Locale, Intl.DateTimeFormat>();
  return (locale) => {
    let formatter = cache.get(locale);
    if (!formatter) {
      formatter = new Intl.DateTimeFormat(INTL_LOCALE[locale], options);
      cache.set(locale, formatter);
    }
    return formatter;
  };
}

const plnFormatter = new Intl.NumberFormat('pl-PL', {
  style: 'currency',
  currency: 'PLN',
  useGrouping: 'always',
});

const monthFormatter = perLocale({ month: 'long', year: 'numeric' });

const dateFormatter = perLocale({
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

function toDate(value: DateLike): Date {
  return value instanceof Date ? value : new Date(String(value));
}

/** A decimal string (never a float) to "1 234,56 zł". */
export function plnAmount(value: string | number | null | undefined): string {
  const n = typeof value === 'string' ? Number(value) : Number(value ?? NaN);
  return plnFormatter.format(Number.isFinite(n) ? n : 0);
}

/** "sierpień 2026" / "August 2026". Empty string for unparseable input. */
export function plnMonth(value: DateLike, locale: Locale): string {
  const d = toDate(value);
  return Number.isFinite(d.getTime()) ? monthFormatter(locale).format(d) : '';
}

/** "12 sie 2026" / "12 Aug 2026". Empty string for unparseable input. */
export function formatDate(value: DateLike, locale: Locale): string {
  const d = toDate(value);
  return Number.isFinite(d.getTime()) ? dateFormatter(locale).format(d) : '';
}

/** Back to "YYYY-MM" for `<input type="month">`. */
export function monthValue(value: DateLike): string {
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4})-(\d{2})/);
    if (m) return `${m[1]}-${m[2]}`;
  }
  const d = toDate(value);
  if (!Number.isFinite(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

const warsawDayFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Warsaw',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const dayFormatter = perLocale({
  timeZone: 'UTC',
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

/**
 * Today as "YYYY-MM-DD" in Poland — the calories app's "today", whatever zone
 * the device clock is set to.
 */
export function warsawToday(now: Date = new Date()): string {
  const parts = warsawDayFormatter.formatToParts(now);
  const part = (type: string) => parts.find((p) => p.type === type)?.value;
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/**
 * "YYYY-MM-DD" moved by whole days. Done in UTC, where every day is 24 hours,
 * so a DST switch cannot skip or repeat one.
 */
export function shiftDay(day: string, days: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** "poniedziałek, 5 października 2026". Empty string for unparseable input. */
export function formatDay(day: string, locale: Locale): string {
  const d = new Date(`${day}T00:00:00Z`);
  return Number.isFinite(d.getTime()) ? dayFormatter(locale).format(d) : '';
}
